import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { PageHeaderSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  useApproveBudget,
  useBudgets,
  useCreateBudget,
  useDeleteBudget,
  useReopenBudget,
} from "~/hooks/contabilidad/useBudgets";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";


import { PresupuestoDialog } from "./components/PresupuestoDialog";
import { PresupuestoTable } from "./components/PresupuestoTable";

import type { Budget, BudgetFormData } from "~/types/contabilidad";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function PresupuestosPage() {
  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState<Budget | null>(null);

  const { data: companies = [] } = useCompanies();
  const { data: budgets = [], isLoading } = useBudgets();

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const createMutation = useCreateBudget();
  const approveMutation = useApproveBudget();
  const reopenMutation = useReopenBudget();
  const deleteMutation = useDeleteBudget();

  const yearOptions = useMemo(
    () =>
      Array.from(new Set(budgets.map((b) => b.fiscal_year))).sort(
        (a, b) => b - a
      ),
    [budgets]
  );

  const filteredBudgets = useMemo(() => {
    return budgets.filter((budget) => {
      const matchesCompany =
        companyFilter === "all" || budget.company_id === companyFilter;
      const matchesYear =
        yearFilter === "all" || budget.fiscal_year.toString() === yearFilter;
      const matchesStatus =
        statusFilter === "all" || budget.status === statusFilter;
      return matchesCompany && matchesYear && matchesStatus;
    });
  }, [budgets, companyFilter, yearFilter, statusFilter]);

  const handleCreate = (data: BudgetFormData) => {
    createMutation.mutate(data, {
      onSuccess: (budget) => {
        setDialogOpen(false);
        navigate(`/contabilidad/presupuestos/${budget.id}`);
      },
    });
  };

  const handleDelete = () => {
    if (!budgetToDelete) return;
    deleteMutation.mutate(budgetToDelete.id, {
      onSettled: () => setBudgetToDelete(null),
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <TableSkeleton rows={6} columns={7} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Presupuestos"
        description="Control presupuestario por cuenta y mes"
        actions={
          <Button onClick={() => setDialogOpen(true)} className="h-9">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Presupuesto
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[180px]">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[140px]">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[140px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="approved">Aprobado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <PresupuestoTable
              budgets={filteredBudgets}
              onOpen={(budget) =>
                navigate(`/contabilidad/presupuestos/${budget.id}`)
              }
              onApprove={(budget) => approveMutation.mutate(budget.id)}
              onReopen={(budget) => reopenMutation.mutate(budget.id)}
              onDelete={(budget) => setBudgetToDelete(budget)}
            />
          </div>
        </CardContent>
      </Card>

      <PresupuestoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        companies={companies}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <AlertDialog
        open={!!budgetToDelete}
        onOpenChange={(open) => !open && setBudgetToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar presupuesto</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{budgetToDelete?.name}" (
              {budgetToDelete?.fiscal_year}). Esta acción no se puede deshacer
              desde la aplicación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
